import React, { useState, useEffect } from 'react';
import { supabase } from '../global/supabase';
import SignatureCanvas from './SignatureCanvas';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

function formatSubstanceName(substance) {
  if (substance === 'sealNumber') return 'Seal Number';
  return substance
    .replace(/_/g, ' ')
    .replace(/(\d+)/g, ' $1')
    .replace(/ml/g, 'mL')
    .replace(/\b\w/g, c => c.toUpperCase())
    .trim();
}

function InventoryModalEMS({ config, userInfo, lastInventory, onClose, onSubmit }) {
  const [sealIntact, setSealIntact] = useState(null);
  const [sealNumber, setSealNumber] = useState('');
  const [newSealNumber, setNewSealNumber] = useState('');
  const [reasonBrokenSeal, setReasonBrokenSeal] = useState('');
  const [medications, setMedications] = useState({});
  const [medicationExpirations, setMedicationExpirations] = useState({});
  const [inventoryPicture, setInventoryPicture] = useState(null);
  const [inventoryPicturePreview, setInventoryPicturePreview] = useState(null);
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Conditional fields for EMS Station
  const [runNumber, setRunNumber] = useState('');
  const [ambulance, setAmbulance] = useState('');
  const [whichMedication, setWhichMedication] = useState('');
  const [amountMoved, setAmountMoved] = useState('');
  const [restockItems, setRestockItems] = useState([]);

  const [primarySignature, setPrimarySignature] = useState(null);
  const [secondarySignature, setSecondarySignature] = useState(null);
  const [showPrimaryCanvas, setShowPrimaryCanvas] = useState(false);
  const [showSecondaryCanvas, setShowSecondaryCanvas] = useState(false);

  const [secondaryLicense, setSecondaryLicense] = useState('');
  const [secondaryPin, setSecondaryPin] = useState('');
  const [secondaryUser, setSecondaryUser] = useState(null);
  const [secondaryValidated, setSecondaryValidated] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [showQuantityWarning, setShowQuantityWarning] = useState(false);
  const [quantityWarningMeds, setQuantityWarningMeds] = useState([]);

  // Initialize medications and expirations from last inventory
  useEffect(() => {
    if (lastInventory) {
      const medData = {};
      const expData = {};
      config.substances.forEach(substance => {
        medData[substance] = lastInventory[substance] || '0';
        // Parse expiration date if exists
        const expValue = lastInventory[`${substance}_exp`];
        expData[substance] = expValue ? new Date(expValue) : null;
      });
      setMedications(medData);
      setMedicationExpirations(expData);
      setSealNumber(lastInventory.seal_number || '');
    }
  }, [lastInventory, config.substances]);

  // Handle seal status change
  const handleSealIntactChange = (value) => {
    setSealIntact(value);
    if (value === 'yes') {
      // Pre-fill with last data
      setSealNumber(lastInventory?.seal_number || '');
      const medData = {};
      const expData = {};
      config.substances.forEach(substance => {
        medData[substance] = lastInventory?.[substance] || '0';
        const expValue = lastInventory?.[`${substance}_exp`];
        expData[substance] = expValue ? new Date(expValue) : null;
      });
      setMedications(medData);
      setMedicationExpirations(expData);
    } else {
      // Clear for new entry
      setNewSealNumber('');
      const medData = {};
      const expData = {};
      config.substances.forEach(substance => {
        medData[substance] = '';
        expData[substance] = null;
      });
      setMedications(medData);
      setMedicationExpirations(expData);
    }
  };

  // Handle reason change - clear conditional fields
  const handleReasonChange = (value) => {
    setReasonBrokenSeal(value);
    // Reset conditional fields
    setRunNumber('');
    setAmbulance('');
    setWhichMedication('');
    setAmountMoved('');
    setRestockItems([]);
  };

  // Handle medication quantity change
  const handleMedicationChange = (substance, value) => {
    setMedications(prev => ({ ...prev, [substance]: value }));
  };

  // Handle expiration date change
  const handleExpirationChange = (substance, date) => {
    setMedicationExpirations(prev => ({ ...prev, [substance]: date }));
  };

  // Restock items management
  const addRestockItem = () => {
    setRestockItems([...restockItems, { medication: '', amount: '' }]);
  };

  const removeRestockItem = (index) => {
    setRestockItems(restockItems.filter((_, i) => i !== index));
  };

  const updateRestockItem = (index, field, value) => {
    const updated = [...restockItems];
    updated[index][field] = value;
    setRestockItems(updated);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setInventoryPicture(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      setInventoryPicturePreview(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const showAlertPopup = (message, type = 'error') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  // The primary signer cannot also act as the secondary (witnessing) signer.
  // Compare against every identifier we have so the same person cannot slip
  // through by matching on any one of id / state license number / email.
  const isSamePersonAsPrimary = (candidate) => {
    if (!candidate || !userInfo) return false;
    const norm = (value) => (value || '').toString().trim().toLowerCase();
    return (
      (!!userInfo.id && !!candidate.id && userInfo.id === candidate.id) ||
      (!!norm(userInfo.stateLicenseNumber) && norm(userInfo.stateLicenseNumber) === norm(candidate.state_license_number)) ||
      (!!norm(userInfo.email) && norm(userInfo.email) === norm(candidate.email))
    );
  };

  // Validate secondary user
  const handleValidateSecondary = async () => {
    if (!secondaryLicense || !secondaryPin) {
      showAlertPopup('Please enter both State License Number and PIN.');
      return;
    }

    if (isSamePersonAsPrimary({ state_license_number: secondaryLicense })) {
      showAlertPopup('The witnessing (secondary) signer must be a different person than the primary signer.');
      return;
    }

    try {
      // Checked server-side (bcrypt): refuses self-witness and inactive users,
      // and locks a license after repeated failures. Never returns the PIN.
      const { data, error } = await supabase.rpc('validate_secondary_user', {
        license_number: secondaryLicense.trim(),
        user_pin: secondaryPin,
      });
      const result = Array.isArray(data) ? data[0] : data;

      if (error || !result?.is_valid) {
        showAlertPopup(error?.message === 'You cannot witness your own entry'
          ? 'The witnessing (secondary) signer must be a different person than the primary signer.'
          : 'Invalid State License Number or PIN.');
        return;
      }

      setSecondaryUser({
        first_name: result.first_name,
        last_name: result.last_name,
        state_license_number: secondaryLicense.trim(),
      });
      setSecondaryValidated(true);
      showAlertPopup('Validation successful!', 'success');
    } catch (err) {
      console.error('Error validating:', err);
      showAlertPopup('Error validating credentials.');
    }
  };

  // Upload image to Supabase Storage
  const uploadImage = async (file, folder) => {
    const randomId = Math.random().toString(36).substring(2, 9);
    const fileName = `${Date.now()}_${randomId}_${file.name}`;
    const { data, error } = await supabase.storage
      .from('inventory')
      .upload(`${folder}/images/${fileName}`, file);

    if (error) throw error;

    // Stored as a storage path; displayed through a short-lived signed URL.
    return data.path;
  };

  // Upload signature (base64) to Supabase Storage
  const uploadSignature = async (base64Data, folder, fileName) => {
    const base64Response = await fetch(base64Data);
    const blob = await base64Response.blob();

    const { data, error } = await supabase.storage
      .from('inventory')
      .upload(`${folder}/signatures/${fileName}`, blob, {
        contentType: 'image/png'
      });

    if (error) throw error;

    // Stored as a storage path; displayed through a short-lived signed URL.
    return data.path;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!sealIntact) {
      showAlertPopup('Please select seal status.');
      return;
    }

    if (sealIntact === 'yes' && !sealNumber) {
      showAlertPopup('Seal number is required.');
      return;
    }

    if (sealIntact === 'no') {
      if (!reasonBrokenSeal) {
        showAlertPopup('Reason for broken seal is required.');
        return;
      }
      if (!newSealNumber) {
        showAlertPopup('New seal number is required.');
        return;
      }

      // Conditional validations based on reason
      if (reasonBrokenSeal === 'Narcotic Truck Restock') {
        if (!runNumber) {
          showAlertPopup('Run Number is required.');
          return;
        }
        if (!ambulance) {
          showAlertPopup('Ambulance selection is required.');
          return;
        }
        if (!whichMedication) {
          showAlertPopup('Which Medication is required.');
          return;
        }
        if (!amountMoved) {
          showAlertPopup('Amount Moved is required.');
          return;
        }
      }

      if (reasonBrokenSeal === 'Expired Medications Removal') {
        if (!whichMedication) {
          showAlertPopup('Which Medication is required.');
          return;
        }
        if (!amountMoved) {
          showAlertPopup('Amount Moved is required.');
          return;
        }
      }

      if (reasonBrokenSeal === 'Medication Restock') {
        if (restockItems.length === 0) {
          showAlertPopup('Add at least one medication for Medication Restock.');
          return;
        }
        const hasEmpty = restockItems.some(item => !item.medication || !item.amount);
        if (hasEmpty) {
          showAlertPopup('All restock items must have medication and amount filled.');
          return;
        }
      }
    }

    if (!primarySignature) {
      showAlertPopup('Primary signature is required.');
      return;
    }

    if (!secondarySignature) {
      showAlertPopup('Secondary signature is required.');
      return;
    }

    if (!secondaryValidated) {
      showAlertPopup('Secondary user must be validated.');
      return;
    }

    if (isSamePersonAsPrimary(secondaryUser)) {
      showAlertPopup('The witnessing (secondary) signer must be a different person than the primary signer.');
      return;
    }

    // Check for medication quantities with 3+ digits (likely entered as concentration instead of vial count)
    const highQuantityMeds = config.substances.filter(substance => {
      const qty = Number(medications[substance]);
      return !isNaN(qty) && qty >= 100;
    });

    if (highQuantityMeds.length > 0 && !showQuantityWarning) {
      setQuantityWarningMeds(highQuantityMeds);
      setShowQuantityWarning(true);
      return;
    }

    setShowQuantityWarning(false);
    setQuantityWarningMeds([]);

    setLoading(true);
    setError('');

    try {
      // Upload images and signatures
      let inventoryPictureUrl = null;
      if (inventoryPicture) {
        inventoryPictureUrl = await uploadImage(inventoryPicture, config.storageFolder);
      }

      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 9);
      const primarySigFileName = `${timestamp}_${randomId}_primary.png`;
      const secondarySigFileName = `${timestamp}_${randomId}_secondary.png`;

      const primarySignatureUrl = await uploadSignature(primarySignature, config.storageFolder, primarySigFileName);
      const secondarySignatureUrl = await uploadSignature(secondarySignature, config.storageFolder, secondarySigFileName);

      // Prepare data for submission
      const submissionData = {
        location: config.locationName,
        seal_intact: sealIntact,
        seal_number: sealIntact === 'yes' ? sealNumber : newSealNumber,
        reason_broken_seal: sealIntact === 'no' ? reasonBrokenSeal : null,
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        email: userInfo.email,
        secondary_name: `${secondaryUser.first_name} ${secondaryUser.last_name}`,
        primary_signature_url: primarySignatureUrl,
        secondary_signature_url: secondarySignatureUrl,
        inventory_picture_url: inventoryPictureUrl,
        created_at: new Date().toISOString(),

        // EMS Station specific fields
        run_number: runNumber || null,
        ambulance: ambulance || null,
        which_medication: whichMedication || null,
        amount_moved: amountMoved || null,
        medication_restock_items_json: restockItems.length > 0 ? JSON.stringify(restockItems) : null
      };

      // Add additional notes if provided
      if (additionalNotes) {
        submissionData.additional_notes = additionalNotes;
      }

      // Add all medication quantities and expiration dates
      config.substances.forEach(substance => {
        submissionData[substance] = medications[substance] || '0';
        // Format date as YYYY-MM-DD for storage
        if (medicationExpirations[substance]) {
          const expDate = medicationExpirations[substance];
          submissionData[`${substance}_exp`] = expDate.toISOString().split('T')[0];
        } else {
          submissionData[`${substance}_exp`] = null;
        }
      });

      // Insert into Supabase
      const { data: insertedData, error } = await supabase
        .from(config.tableName)
        .insert([submissionData])
        .select('id');

      if (error) throw error;

      // Station change detection - does not block form submission
      try {
        const stationTables = ['ems_station', 'ems_station_expired'];
        if (stationTables.includes(config.tableName) && insertedData && insertedData[0]) {
          const { data: sessionData } = await supabase.auth.getSession();
          const token = sessionData?.session?.access_token;
          const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://jwkmhgishqslmdlbhqqz.supabase.co';
          fetch(`${supabaseUrl}/functions/v1/detect-station-change`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ table: config.tableName, record_id: insertedData[0].id }),
          });
        }
      } catch (detectErr) {
        console.error('Station change detection error (non-blocking):', detectErr);
      }

      showAlertPopup('Seal check logged successfully!', 'success');
      setTimeout(() => {
        onSubmit();
      }, 1500);
    } catch (err) {
      console.error('Error submitting:', err);
      showAlertPopup(`Error: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0, 0, 0, 0.7)',
      overflowY: 'auto',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '40px',
        maxWidth: '800px',
        margin: '0 auto',
        position: 'relative'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h2 style={{ color: '#333', fontSize: '24px', margin: 0 }}>
            Change Inventory - {config.locationName}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Seal Status */}
        <div style={{ marginBottom: '30px' }}>
          <h3 className="section-title">
            Seal Status <span style={{ color: 'red' }}>*</span>
          </h3>
          <div style={{ display: 'flex', gap: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="seal-status"
                value="yes"
                checked={sealIntact === 'yes'}
                onChange={() => handleSealIntactChange('yes')}
                style={{ marginRight: '8px' }}
              />
              Seal Intact - Yes
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="radio"
                name="seal-status"
                value="no"
                checked={sealIntact === 'no'}
                onChange={() => handleSealIntactChange('no')}
                style={{ marginRight: '8px' }}
              />
              Seal Intact - No
            </label>
          </div>
        </div>

        {/* Seal Number (if intact) */}
        {sealIntact === 'yes' && (
          <div style={{ marginBottom: '30px' }}>
            <label className="form-label">Current Seal Number</label>
            <input
              type="text"
              className="form-input"
              value={sealNumber}
              disabled
              style={{ background: '#f8f9fa' }}
            />
          </div>
        )}

        {/* Broken Seal Info */}
        {sealIntact === 'no' && (
          <>
            <div style={{ marginBottom: '20px' }}>
              <label className="form-label">
                Reason for Broken Seal <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                className="form-input"
                value={reasonBrokenSeal}
                onChange={(e) => handleReasonChange(e.target.value)}
              >
                <option value="">Select a reason</option>
                <option value="Narcotic Truck Restock">Narcotic Truck Restock</option>
                <option value="Expired Medications Removal">Expired Medications Removal</option>
                <option value="Medication Restock">Medication Restock</option>
                <option value="Inspections">Inspections</option>
              </select>
            </div>

            {/* Conditional Fields - Narcotic Truck Restock */}
            {reasonBrokenSeal === 'Narcotic Truck Restock' && (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <label className="form-label">
                    Run Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={runNumber}
                    onChange={(e) => setRunNumber(e.target.value)}
                    placeholder="Enter run number"
                  />
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label className="form-label">
                    Ambulance <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    className="form-input"
                    value={ambulance}
                    onChange={(e) => setAmbulance(e.target.value)}
                  >
                    <option value="">Select Ambulance</option>
                    <option value="Mayo 21">Mayo 21</option>
                    <option value="Mayo 22">Mayo 22</option>
                    <option value="Mayo 23">Mayo 23</option>
                    <option value="Mayo 24">Mayo 24</option>
                    <option value="Mayo 25">Mayo 25</option>
                  </select>
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <label className="form-label">
                    Which Medication <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    className="form-input"
                    value={whichMedication}
                    onChange={(e) => setWhichMedication(e.target.value)}
                  >
                    <option value="">Select Medication</option>
                    {config.substances.map(substance => (
                      <option key={substance} value={substance}>
                        {formatSubstanceName(substance)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label">
                    Amount Moved <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={amountMoved}
                    onChange={(e) => setAmountMoved(e.target.value)}
                    placeholder="Enter amount"
                    min="0"
                  />
                </div>
              </>
            )}

            {/* Conditional Fields - Expired Medications Removal */}
            {reasonBrokenSeal === 'Expired Medications Removal' && (
              <>
                <div style={{ marginBottom: '15px' }}>
                  <label className="form-label">
                    Which Medication <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    className="form-input"
                    value={whichMedication}
                    onChange={(e) => setWhichMedication(e.target.value)}
                  >
                    <option value="">Select Medication</option>
                    {config.substances.map(substance => (
                      <option key={substance} value={substance}>
                        {formatSubstanceName(substance)}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label">
                    Amount Removed <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={amountMoved}
                    onChange={(e) => setAmountMoved(e.target.value)}
                    placeholder="Enter amount"
                    min="0"
                  />
                </div>
              </>
            )}

            {/* Conditional Fields - Medication Restock (Multi-entry) */}
            {reasonBrokenSeal === 'Medication Restock' && (
              <div style={{ marginBottom: '20px' }}>
                <label className="form-label">
                  Restock Items <span style={{ color: 'red' }}>*</span>
                </label>

                {restockItems.map((item, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr auto',
                    gap: '10px',
                    marginBottom: '10px',
                    padding: '15px',
                    background: '#f8f9fa',
                    borderRadius: '8px'
                  }}>
                    <select
                      className="form-input"
                      value={item.medication}
                      onChange={(e) => updateRestockItem(index, 'medication', e.target.value)}
                      style={{ margin: 0 }}
                    >
                      <option value="">Select Medication</option>
                      {config.substances.map(substance => (
                        <option key={substance} value={substance}>
                          {formatSubstanceName(substance)}
                        </option>
                      ))}
                    </select>

                    <input
                      type="number"
                      className="form-input"
                      value={item.amount}
                      onChange={(e) => updateRestockItem(index, 'amount', e.target.value)}
                      placeholder="Amount"
                      min="0"
                      style={{ margin: 0 }}
                    />

                    <button
                      className="btn btn-secondary"
                      onClick={() => removeRestockItem(index)}
                      style={{
                        padding: '8px 15px',
                        fontSize: '14px',
                        background: '#dc3545',
                        borderColor: '#dc3545'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <button
                  className="btn btn-primary"
                  onClick={addRestockItem}
                  style={{ marginTop: '10px' }}
                >
                  + Add Restock Item
                </button>
              </div>
            )}

            <div style={{ marginBottom: '30px' }}>
              <label className="form-label">
                New Seal Number <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                className="form-input"
                value={newSealNumber}
                onChange={(e) => setNewSealNumber(e.target.value)}
                placeholder="Enter new seal number"
              />
            </div>

            {/* Medication Inventory */}
            <div style={{ marginBottom: '30px' }}>
              <h3 className="section-title">Medication Inventory</h3>
              <div style={{ display: 'grid', gap: '15px' }}>
                {config.substances.map(substance => (
                  <div key={substance} style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr',
                    gap: '10px',
                    alignItems: 'center'
                  }}>
                    <label className="form-label" style={{ fontSize: '14px', margin: 0 }}>
                      {formatSubstanceName(substance)}
                    </label>
                    <input
                      type="number"
                      className="form-input"
                      value={medications[substance] || ''}
                      onChange={(e) => handleMedicationChange(substance, e.target.value)}
                      placeholder="Qty"
                      min="0"
                      style={{ margin: 0 }}
                    />
                    <DatePicker
                      selected={medicationExpirations[substance]}
                      onChange={(date) => handleExpirationChange(substance, date)}
                      dateFormat="MM/dd/yyyy"
                      placeholderText="Exp Date"
                      className="form-input"
                      style={{ margin: 0 }}
                      wrapperClassName="date-picker-wrapper"
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Inventory Picture */}
        <div style={{ marginBottom: '30px' }}>
          <h3 className="section-title">Inventory Picture</h3>
          <label
            htmlFor="file-upload"
            className="btn btn-secondary"
            style={{
              display: 'inline-block',
              cursor: 'pointer',
              textAlign: 'center'
            }}
          >
            {inventoryPicturePreview ? 'Change Picture' : 'Take/Upload Picture'}
          </label>
          <input
            id="file-upload"
            type="file"
            accept="image/*"
            capture="camera"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          {inventoryPicturePreview && (
            <img
              src={inventoryPicturePreview}
              alt="Inventory Preview"
              style={{
                maxWidth: '300px',
                width: '100%',
                borderRadius: '8px',
                marginTop: '15px',
                border: '2px solid #e0e0e0'
              }}
            />
          )}
        </div>

        {/* Primary Signature */}
        <div style={{ marginBottom: '30px' }}>
          <h3 className="section-title">
            Primary Signature <span style={{ color: 'red' }}>*</span>
          </h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            {userInfo.firstName} {userInfo.lastName} ({userInfo.email})
          </p>
          {primarySignature ? (
            <div>
              <img src={primarySignature} alt="Primary Signature" style={{ width: '200px', border: '1px solid #ddd', borderRadius: '8px' }} />
              <button
                className="btn btn-secondary"
                onClick={() => setShowPrimaryCanvas(true)}
                style={{ marginTop: '10px' }}
              >
                Re-sign
              </button>
            </div>
          ) : (
            <button
              className="btn btn-primary"
              onClick={() => setShowPrimaryCanvas(true)}
            >
              Add Primary Signature
            </button>
          )}
        </div>

        {/* Secondary User Validation */}
        <div style={{ marginBottom: '30px' }}>
          <h3 className="section-title">
            Secondary User Validation <span style={{ color: 'red' }}>*</span>
          </h3>
          {!secondaryValidated ? (
            <>
              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  State License Number <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={secondaryLicense}
                  onChange={(e) => setSecondaryLicense(e.target.value)}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label className="form-label">
                  PIN <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="password"
                  className="form-input"
                  value={secondaryPin}
                  onChange={(e) => setSecondaryPin(e.target.value)}
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleValidateSecondary}
              >
                Confirm License and PIN
              </button>
            </>
          ) : (
            <div style={{ background: '#e8f5e8', padding: '15px', borderRadius: '8px' }}>
              <p style={{ color: '#2e7d32', fontWeight: '600', marginBottom: '5px' }}>
                ✓ Validated
              </p>
              <p style={{ fontSize: '14px', color: '#333' }}>
                {secondaryUser.first_name} {secondaryUser.last_name}
              </p>
            </div>
          )}
        </div>

        {/* Secondary Signature */}
        {secondaryValidated && (
          <div style={{ marginBottom: '30px' }}>
            <h3 className="section-title">
              Secondary Signature <span style={{ color: 'red' }}>*</span>
            </h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
              {secondaryUser.first_name} {secondaryUser.last_name}
            </p>
            {secondarySignature ? (
              <div>
                <img src={secondarySignature} alt="Secondary Signature" style={{ width: '200px', border: '1px solid #ddd', borderRadius: '8px' }} />
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSecondaryCanvas(true)}
                  style={{ marginTop: '10px' }}
                >
                  Re-sign
                </button>
              </div>
            ) : (
              <button
                className="btn btn-primary"
                onClick={() => setShowSecondaryCanvas(true)}
              >
                Add Secondary Signature
              </button>
            )}
          </div>
        )}

        {/* Additional Notes */}
        <div style={{ marginBottom: '30px' }}>
          <h3 className="section-title">Additional Notes</h3>
          <textarea
            className="form-input"
            value={additionalNotes}
            onChange={(e) => setAdditionalNotes(e.target.value)}
            rows="4"
            placeholder="Enter any additional notes..."
          />
        </div>

        {/* Submit Button */}
        <button
          className="btn btn-primary btn-full"
          onClick={handleSubmit}
          disabled={loading}
          style={{ padding: '15px', fontSize: '18px' }}
        >
          {loading ? 'Submitting...' : 'Submit Seal Check'}
        </button>
      </div>

      {/* Signature Canvases */}
      {showPrimaryCanvas && (
        <SignatureCanvas
          title="Primary Signature"
          onConfirm={(sig) => {
            setPrimarySignature(sig);
            setShowPrimaryCanvas(false);
          }}
          onCancel={() => setShowPrimaryCanvas(false)}
        />
      )}

      {showSecondaryCanvas && (
        <SignatureCanvas
          title="Secondary Signature"
          onConfirm={(sig) => {
            setSecondarySignature(sig);
            setShowSecondaryCanvas(false);
          }}
          onCancel={() => setShowSecondaryCanvas(false)}
        />
      )}

      {/* Loading Spinner */}
      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          flexDirection: 'column'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '6px solid rgba(255, 255, 255, 0.3)',
            borderTop: '6px solid white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{
            color: 'white',
            marginTop: '20px',
            fontSize: '16px',
            fontWeight: '500'
          }}>
            Submitting...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Alert Popup */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              backgroundColor: alertType === 'success' ? '#e8f5e8' : alertType === 'error' ? '#fee' : '#e3f2fd',
              color: alertType === 'success' ? '#2e7d32' : alertType === 'error' ? '#c33' : '#1976d2'
            }}>
              {alertType === 'success' ? '✓' : alertType === 'error' ? '✕' : 'ℹ'}
            </div>
            <h3 style={{
              margin: '0 0 15px',
              fontSize: '20px',
              color: '#333'
            }}>
              {alertType === 'success' ? 'Success' : alertType === 'error' ? 'Error' : 'Notice'}
            </h3>
            <p style={{
              margin: '0 0 25px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              {alertMessage}
            </p>
            <button
              onClick={() => setShowAlert(false)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Quantity Warning Modal */}
      {showQuantityWarning && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              backgroundColor: '#fff3e0',
              color: '#e65100'
            }}>
              !
            </div>
            <h3 style={{
              margin: '0 0 10px',
              fontSize: '20px',
              color: '#333'
            }}>
              Quantity Check
            </h3>
            <p style={{
              margin: '0 0 15px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              The following medication(s) have a quantity of <strong>100 or more</strong>. Inventory should reflect the <strong>number of vials</strong>, not the total medication concentration.
            </p>
            <div style={{
              background: '#fff3e0',
              border: '1px solid #ffe0b2',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              {quantityWarningMeds.map(med => (
                <div key={med} style={{
                  padding: '6px 0',
                  borderBottom: '1px solid #ffe0b2',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '600', color: '#333' }}>{formatSubstanceName(med)}</span>
                  <span style={{
                    background: '#e65100',
                    color: '#fff',
                    padding: '2px 10px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    {medications[med]}
                  </span>
                </div>
              ))}
            </div>
            <p style={{
              margin: '0 0 20px',
              color: '#888',
              fontSize: '13px',
              lineHeight: '1.4'
            }}>
              Please verify these quantities are correct before continuing. If they represent concentration values, go back and enter the vial count instead.
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => {
                  setShowQuantityWarning(false);
                  setQuantityWarningMeds([]);
                }}
                className="btn btn-secondary"
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '16px',
                  background: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600'
                }}
              >
                Cancel & Fix
              </button>
              <button
                onClick={() => handleSubmit()}
                className="btn btn-primary"
                style={{
                  flex: 1,
                  padding: '12px',
                  fontSize: '16px',
                  borderRadius: '8px',
                  fontWeight: '600'
                }}
              >
                Confirm & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InventoryModalEMS;
